import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Helmet } from 'react-helmet';
import './LoginForm.css';

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor, ingresa tu correo y contraseña.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (loginError) {
        if (loginError.message.includes('Invalid login credentials')) {
          setError('Correo o contraseña incorrectos.');
        } else if (loginError.message.includes('Email not confirmed')) {
          setError('Tu correo aún no ha sido confirmado. Por favor revisa tu bandeja de entrada.');
        } else {
          setError(loginError.message || 'Error al iniciar sesión. Inténtalo de nuevo.');
        }
        return;
      }

      if (data?.user && onLogin) {
        onLogin({ email: email.trim(), password });
      }

      const redirectPath = location.state?.from || '/';
      navigate(redirectPath);
    } catch (err) {
      console.error('Error al iniciar sesión:', err);
      setError('Ocurrió un error inesperado al iniciar sesión.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20 relative bg-dark-grid">
      <Helmet>
        <title>Ingresar - MatiMarket</title>
        <meta name="description" content="Inicia sesión en MatiMarket para acceder a tus puntos, sorteos y productos exclusivos." />
      </Helmet>

      {/* Decorative ambient glows */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10 glass-card p-8 rounded-2xl border border-gold/30 shadow-2xl backdrop-blur-xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/10 border border-gold/40 mb-3 text-gold shadow-lg shadow-gold/20">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
          </div>
          <h1 className="text-2xl font-black text-white uppercase tracking-wider">
            Bienvenido a <span className="text-gold-gradient">MatiMarket</span>
          </h1>
          <p className="text-sm text-gray-300 mt-1">Ingresa tus datos para acceder a tu cuenta</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 text-sm flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label htmlFor="login-email" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
              Correo Electrónico
            </label>
            <input
              id="login-email"
              type="email"
              required
              placeholder="tu@correo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="login-password" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider">
                Contraseña
              </label>
              <Link
                to="/recuperar-contrasena"
                className="text-xs text-gold hover:text-gold-light transition hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <input
              id="login-password"
              type="password"
              required
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3.5 px-4 rounded-xl font-bold uppercase tracking-wider text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 shadow-lg shadow-gold/25 hover:shadow-gold/40 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 mt-6"
          >
            {isSubmitting ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                <span>Iniciando sesión...</span>
              </>
            ) : (
              'Ingresar'
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-800 text-center text-sm text-gray-400">
          ¿Aún no tienes una cuenta?{' '}
          <Link to="/registrate" className="text-gold font-bold hover:underline">
            Regístrate gratis
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;